import { Card } from "@closer/ui";
import { Link } from "react-router";

import { ContractAiAnalysisSection } from "./ContractAiAnalysisSection";
import { ContractAiRiskSection } from "./ContractAiRiskSection";
import { ContractCommentsSection } from "./ContractCommentsSection";
import { ContractContentPage } from "./ContractContentPage";
import { useContractDetail } from "./contractDetailContext";
import { ContractNegotiationSection } from "./ContractNegotiationSection";
import { ContractObjectSharesSection } from "./ContractObjectSharesSection";

/**
 * Belge yüzeylerine giden bağlantıların ortak sınıfı.
 *
 * Altı çizili düz metin yerine çip: bağlantılar yan yana altı çizili yazıldığında bir
 * cümleye benziyor, hangisinin tıklanabilir olduğu ancak imleç üstüne gelince anlaşılıyordu.
 */
const BELGE_BAGLANTISI =
  "inline-flex items-center rounded-lg border border-border-default bg-surface px-3 py-1.5 text-sm font-medium text-fg-strong transition-colors hover:border-primary-muted hover:bg-primary-soft hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary";

/**
 * BELGE bölümü — sözleşme metninin ÇALIŞMA YÜZEYİ.
 *
 * ### ⚠ Metin artık bir düğmenin arkasında değil
 *
 * Önceki hâlinde bu sayfa bir KAPIYDI: dört bağlantı çipi ve altında metne dair kartlar.
 * Metnin kendisi ayrı bir rotadaydı (`/metin`) ve kullanıcı bir CLM ürününde sözleşmenin
 * gövdesine ulaşmak için ekran değiştiriyordu. Şimdi metin bu bölümün kendisi; `/metin`
 * adresi korunuyor ama buraya yönlendiriyor (eski bağlantılar ve derin linkler kırılmasın).
 *
 * ### ⚠ İKİ SÜTUN: solda metin, sağda bağlam
 *
 * Hedef tasarımın İçerik ekranı tam olarak bunu yapıyor — belge ortada, sağında yorumlar,
 * revizyon önerileri ve AI analizi. Sebebi de orada görünüyor: karşı taraftan gelen bir
 * madde önerisini değerlendirmek için maddenin METNİNE, ÖNERİYE ve o maddenin YORUMUNA
 * aynı anda bakmak gerekiyor. Üçü ayrı yüzeydeyken kullanıcı aralarında gidip geliyordu.
 *
 * ⚠ Sütunlar `lg` altında ALT ALTA düşüyor ve metin ÖNCE geliyor: dar ekranda bağlam
 * paneli metnin üstüne çıksaydı, kullanıcı okumak istediği şeye ulaşmak için üç kartı
 * geçmek zorunda kalırdı.
 *
 * ### ⚠ Versiyon/karşılaştırma AYRI ROTALARDA kalıyor
 *
 * İkisi de tam genişlik isteyen ekranlar (yan yana iki sürümün farkı, sürüm listesi) ve
 * bir yan panele sıkıştırılamaz. Bağlantıları rayın en altında; oradan dönüş yine bu
 * bölüme.
 *
 * ### ⚠ AI BURADA, Süreç'te değil
 *
 * Analiz bir KARAR halkası değil, bir OKUMA yardımı (KARAR 11: *"kendiliğinden
 * versiyon/karar doğurmaz"*). İnceleme/onay/mutabakat/imza zincirinin arasına koysaydık,
 * ekran onu o zincirin bir adımı gibi gösterirdi. Çıktısı metin hakkında olduğu için
 * metnin yanında.
 */
export function ContractDocumentHubPage() {
  const { id, s, arsivde, kayitYazma } = useContractDetail();

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      {/*
        ⚠ METİN KENDİ KARTINDA: editör yüzeyi (araç çubuğu, madde blokları, kaydetme
        durumu) bir yüzey istiyor ve sağdaki rayla aynı görsel düzlemde durmalı.
      */}
      <Card>
        <ContractContentPage />
      </Card>

      <div className="flex flex-col gap-4">
        <ContractNegotiationSection contractId={id} />
        {/*
          KARŞI TARAFA AÇILAN SÜRÜMLER müzakerenin hemen altında (CLOS-1128 · Halit K1):
          sürüm bazlı karşı taraf iş birliği belge/versiyon yüzeyinin işi, taraf kimliğinin
          değil. Yönetemeyen kullanıcıya bölüm çizilmez.
        */}
        <ContractObjectSharesSection contractId={id} />
        <ContractCommentsSection contractId={id} kayitYazma={kayitYazma} />
        <ContractAiAnalysisSection contractId={id} arsivde={arsivde} kayitYazma={kayitYazma} />
        <ContractAiRiskSection contractId={id} arsivde={arsivde} kayitYazma={kayitYazma} />

        <Card>
          <div className="flex flex-col gap-2">
            <h2 className="text-base font-semibold text-fg-heading">Sürümler</h2>
            <p className="text-sm text-fg-muted">
              Sürüm geçmişi ve iki sürüm arasındaki fark, tam genişlik isteyen ayrı ekranlarda
              açılıyor.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {/* Versiyon geçmişi (`[090]`) — metadata görünümü; içerik teslimi ayrı sözleşme. */}
              <Link className={BELGE_BAGLANTISI} to={`/app/sozlesmeler/${s.id}/versiyonlar`}>
                Versiyon geçmişi
              </Link>
              <Link className={BELGE_BAGLANTISI} to={`/app/sozlesmeler/${s.id}/karsilastir`}>
                Sürümleri karşılaştır
              </Link>
              {/* Revizyon önerileri (`[093]`/`[094]`) — kaydın bir yüzü, kendi adresi var. */}
              <Link className={BELGE_BAGLANTISI} to={`/app/sozlesmeler/${s.id}/revizyonlar`}>
                Tüm revizyon önerileri
              </Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
